import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Lightbulb,
  TriangleAlert,
  Info,
  Sparkles,
  CircleX,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { parseMeshpitHref } from '@/utils/parameterUtils';
import type { MarkdownVariant } from '@shared/types';

/**
 * URL transform that extends react-markdown's default safe-protocol allowlist
 * with the `meshpit:` scheme so action links are not stripped before the
 * custom `a` component renderer receives them.
 *
 * All other URLs still go through `defaultUrlTransform`, which blocks
 * `javascript:`, `data:`, and other unsafe schemes.
 */
function urlTransform(url: string): string {
  if (url.startsWith('meshpit:')) return url;
  return defaultUrlTransform(url);
}

/**
 * Strict whitelist of HTML elements that may be rendered from markdown.
 * Raw HTML in the source is already stripped by react-markdown's default
 * behaviour (no dangerouslySetInnerHTML). This allowedElements list is an
 * additional belt-and-suspenders guard: even if a future plugin or react-markdown
 * version were to re-introduce HTML handling, only these safe elements pass.
 *
 * `a` is included here but its rendering is fully overridden by the custom
 * components map below — only `meshpit:` hrefs produce interactive elements;
 * all other hrefs are rendered as plain text (preserving the link label).
 */
const ALLOWED_ELEMENTS: string[] = [
  'p',
  'br',
  'strong',
  'em',
  'del',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'hr',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'a',
];

// ---------------------------------------------------------------------------
// Variant config
// ---------------------------------------------------------------------------

type VariantConfig = {
  icon: LucideIcon;
  label: string;
  border: string;
  bg: string;
  headerBorder: string;
  iconColor: string;
  headerText: string;
};

const VARIANT_CONFIG: Record<MarkdownVariant, VariantConfig> = {
  idea: {
    icon: Lightbulb,
    label: 'Idea',
    border: 'border-indigo-500/40',
    bg: 'bg-indigo-500/10',
    headerBorder: 'border-indigo-500/30',
    iconColor: 'text-indigo-500',
    headerText: 'text-indigo-600 dark:text-indigo-400',
  },
  warning: {
    icon: TriangleAlert,
    label: 'Warning',
    border: 'border-amber-500/40',
    bg: 'bg-amber-500/10',
    headerBorder: 'border-amber-500/30',
    iconColor: 'text-amber-500',
    headerText: 'text-amber-600 dark:text-amber-400',
  },
  note: {
    icon: Info,
    label: 'Note',
    border: 'border-blue-500/40',
    bg: 'bg-blue-500/10',
    headerBorder: 'border-blue-500/30',
    iconColor: 'text-blue-500',
    headerText: 'text-blue-600 dark:text-blue-400',
  },
  tip: {
    icon: Sparkles,
    label: 'Tip',
    border: 'border-green-500/40',
    bg: 'bg-green-500/10',
    headerBorder: 'border-green-500/30',
    iconColor: 'text-green-500',
    headerText: 'text-green-600 dark:text-green-400',
  },
  error: {
    icon: CircleX,
    label: 'Error',
    border: 'border-red-500/40',
    bg: 'bg-red-500/10',
    headerBorder: 'border-red-500/30',
    iconColor: 'text-red-500',
    headerText: 'text-red-600 dark:text-red-400',
  },
};

// ---------------------------------------------------------------------------
// Shared prose classes
// ---------------------------------------------------------------------------

const PROSE_CLASSES =
  'prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-headings:text-foreground prose-p:text-muted-foreground prose-blockquote:border-border prose-blockquote:text-muted-foreground prose-strong:text-foreground prose-em:text-muted-foreground prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:text-xs prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-li:text-muted-foreground prose-hr:border-border [&>:first-child]:mt-0 [&>:last-child]:mb-0';

// ---------------------------------------------------------------------------
// ActionBadge
// ---------------------------------------------------------------------------

export interface MarkdownBlockProps {
  content: string;
  variant?: MarkdownVariant;
  /**
   * Called when a `meshpit:snapshot:<id>` link is clicked.
   * If omitted, snapshot links are rendered as inert badges.
   */
  onApplySnapshot?: (snapshotId: string) => void;
  /**
   * Called when a `meshpit:<paramName>:<value>` link is clicked.
   * If omitted, parameter links are rendered as inert badges.
   */
  onSetParameter?: (name: string, value: string) => void;
}

/**
 * Pill-shaped action badge used for `meshpit:` links.
 * When `onClick` is provided the badge is interactive; otherwise it is inert
 * (used in contexts such as blog posts where no panel is available).
 */
export function ActionBadge({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="inline-flex cursor-pointer items-center rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20 disabled:cursor-default disabled:opacity-60"
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Shared ReactMarkdown component renderer map
// ---------------------------------------------------------------------------

function makeComponents(
  onApplySnapshot?: (id: string) => void,
  onSetParameter?: (name: string, value: string) => void,
) {
  return {
    // Override <a> rendering: meshpit: links become action badges;
    // all other links are rendered as plain text (the children) only —
    // we never emit navigable hrefs to prevent javascript:/data: abuse.
    a({ href = '', children }: { href?: string; children?: React.ReactNode }) {
      const action = parseMeshpitHref(href);

      if (action.type === 'snapshot') {
        const handler = onApplySnapshot
          ? () => onApplySnapshot(action.id)
          : undefined;
        return <ActionBadge onClick={handler}>{children}</ActionBadge>;
      }

      if (action.type === 'parameter') {
        const handler = onSetParameter
          ? () => onSetParameter(action.name, action.value)
          : undefined;
        return <ActionBadge onClick={handler}>{children}</ActionBadge>;
      }

      // Non-meshpit links: render label text only, no anchor element
      return <>{children}</>;
    },
  };
}

// ---------------------------------------------------------------------------
// MarkdownBlock
// ---------------------------------------------------------------------------

/**
 * Renders a markdown string as styled content inside the parameter panel.
 *
 * Safety guarantees:
 * - react-markdown v10 never uses dangerouslySetInnerHTML; raw HTML in
 *   the markdown source is silently dropped.
 * - allowedElements enforces an explicit element whitelist as an extra guard.
 * - `<a>` tags are allowed but fully overridden: only `meshpit:` hrefs
 *   produce interactive elements; all other hrefs are rendered as plain text.
 * - No image tags, so no external resource loading.
 */
export function MarkdownBlock({
  content,
  variant,
  onApplySnapshot,
  onSetParameter,
}: MarkdownBlockProps) {
  const components = makeComponents(onApplySnapshot, onSetParameter);

  const markdownContent = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      allowedElements={ALLOWED_ELEMENTS}
      unwrapDisallowed
      urlTransform={urlTransform}
      components={components}
    >
      {content}
    </ReactMarkdown>
  );

  // Plain (no variant) — original neutral style
  if (!variant) {
    return (
      <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2.5 shadow-sm">
        <div className={PROSE_CLASSES}>{markdownContent}</div>
      </div>
    );
  }

  // Variant callout box
  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;

  return (
    <div
      className={`rounded-md border ${config.border} ${config.bg} shadow-sm`}
    >
      {/* Header row: icon + label */}
      <div
        className={`flex items-center gap-1.5 border-b ${config.headerBorder} px-3 py-1.5`}
      >
        <Icon className={`h-3.5 w-3.5 shrink-0 ${config.iconColor}`} />
        <span className={`text-xs font-semibold ${config.headerText}`}>
          {config.label}
        </span>
      </div>
      {/* Content */}
      <div className="px-3 py-2.5">
        <div className={PROSE_CLASSES}>{markdownContent}</div>
      </div>
    </div>
  );
}
